/**
 * Task #2879 — Tab Cattura tshark KAM-1 (SIP Cluster master).
 *
 * Tipi condivisi backend/frontend per il bridge SSH→SSE che esegue `tshark`
 * sul SBC KAM-1 con argomenti vincolati (gruppo-porta da whitelist, output
 * testuale a riga singola, mai scrittura su file).
 *
 * Il gruppo-porta è una WHITELIST: il backend invia solo la chiave del gruppo
 * al wrapper `efai-capture` su KAM-1, che la mappa a un filtro BPF hardcoded.
 * Nessun filtro BPF arbitrario è esposto all'utente.
 */
import { z } from 'zod';

/**
 * Gruppi-porta predefiniti catturabili. Whitelist chiusa:
 *  - sip-signaling : SIP signaling (5060 UDP/TCP, 5061 TLS)
 *  - rtp-media     : RTP media (range rtpengine 16384-32768)
 *  - lan-internal  : traffico LAN interno verso i nodi FreeSWITCH (segnalazione/relay)
 */
export const SIP_CAPTURE_GROUPS = ['sip-signaling', 'rtp-media', 'lan-internal'] as const;
export type SipCaptureGroup = (typeof SIP_CAPTURE_GROUPS)[number];

export const SipCaptureGroupSchema = z.enum(SIP_CAPTURE_GROUPS);

/** Etichette UI + descrizione del gruppo-porta (singola fonte). */
export interface SipCaptureGroupMeta {
  group: SipCaptureGroup;
  label: string;
  description: string;
}

export const SIP_CAPTURE_GROUP_META: readonly SipCaptureGroupMeta[] = [
  {
    group: 'sip-signaling',
    label: 'SIP Signaling',
    description: 'Segnalazione SIP — porte 5060 (UDP/TCP) e 5061 (TLS)',
  },
  {
    group: 'rtp-media',
    label: 'RTP Media',
    description: 'Media RTP/RTCP — range rtpengine 16384-32768',
  },
  {
    group: 'lan-internal',
    label: 'LAN interna → FreeSWITCH',
    description: 'Traffico LAN interno verso i nodi FreeSWITCH (segnalazione/relay)',
  },
] as const;

/** Body POST /api/master/voip-cluster/capture/start. */
export const SipCaptureStartRequestSchema = z.object({
  group: SipCaptureGroupSchema,
});
export type SipCaptureStartRequest = z.infer<typeof SipCaptureStartRequestSchema>;

/** Stato corrente del singleton di cattura sul nodo. */
export interface SipCaptureStatus {
  /** Feature configurata sul backend (chiave SSH + host KAM-1 presenti). */
  configured: boolean;
  /** Processo `tshark` attivo sul nodo. */
  running: boolean;
  /** Nodo target (sempre KAM-1 in questo task). */
  nodeId: string;
  /** Gruppo-porta attualmente in cattura, null se ferma. */
  group: SipCaptureGroup | null;
  /** ISO timestamp avvio cattura, null se ferma. */
  startedAt: string | null;
  /** Email dell'operatore che ha avviato, null se ferma. */
  startedBy: string | null;
}

// ---------------------------------------------------------------------------
// Payload SSE (event-name = `type`), consumati dal tab "Cattura".
// ---------------------------------------------------------------------------

/** Snapshot di stato inviato all'apertura dello stream e su ogni transizione. */
export interface SipCaptureStatusEvent {
  type: 'status';
  status: SipCaptureStatus;
}

/** Singola riga di output `tshark` (un pacchetto). */
export interface SipCaptureLineEvent {
  type: 'line';
  /** Sequenza monotona crescente per la sessione di cattura corrente. */
  seq: number;
  ts: string;
  group: SipCaptureGroup;
  line: string;
}

/** Errore non fatale (es. stderr del wrapper, ssh non raggiungibile). */
export interface SipCaptureErrorEvent {
  type: 'error';
  ts: string;
  message: string;
}

export type SipCaptureEvent =
  | SipCaptureStatusEvent
  | SipCaptureLineEvent
  | SipCaptureErrorEvent;
